import { useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { useEffectOnce } from 'react-use';
import { Button, Checkbox, cn, Popover } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import { useMarketHistoryLayoutOutletContext } from '@/enterprise/components/MarketHistoryLayout';
import Skeleton from '@/enterprise/components/Skeleton';
import { TableSortDirection } from '@/enterprise/components/Table';
import CreditNoteModal from '@/enterprise/features/history/CreditNoteModal';
import HistoryChart from '@/enterprise/features/history/HistoryChart';
import HistoryFilter from '@/enterprise/features/history/HistoryFilter';
import HistoryStatsBar from '@/enterprise/features/history/HistoryStatsBar';
import HistoryTable from '@/enterprise/features/history/HistoryTable';
import RangeHeader from '@/enterprise/features/history/RangeHeader';
import useMarketHistoryData from '@/enterprise/features/history/useMarketHistoryData';
import { dateRanges, getRangeKey } from '@/enterprise/features/history/utils';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { dateAsIsoString } from '@/utils/dateAsIsoString';
import { useToken } from '@/utils/token';
import useUrlState from '@/utils/useUrlState';

// When includeFuturePayDates is true, an end date will not be sent in the request for clear data
// This is applied when querying for clear data using pay date or deposit date
export interface MarketHistoryUrlState {
  endDate: string;
  includeFuturePayDates: boolean;
  limit: number;
  page: number;
  range: string;
  sort: string;
  sortDirection: TableSortDirection;
  startDate: string;
}

const prefixUrl = import.meta.env.VITE_GATEWAY_URL;
const { last30Days: defaultRange } = dateRanges;
// update default sort to use deposit date, updating fallback from pay date to deposit date now that good funds date project is fully released
// sorting is handled in the useMarketHistoryData hook if a user loads a old sort by pay date url
const defaultState = {
  endDate: defaultRange.endDate,
  includeFuturePayDates: true,
  limit: 25,
  page: 1,
  range: 'last30Days',
  sort: 'depositDate',
  sortDirection: 'desc' as TableSortDirection,
  startDate: defaultRange.startDate,
} satisfies MarketHistoryUrlState;

const MarketHistoryComponent = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const {
    takerMarket,
    urlParams: { marketId, takerId },
  } = useMarketHistoryLayoutOutletContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { trackEnterpriseEvent } = useReporting();
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [creditNoteDownloadUrl, setCreditNoteDownloadUrl] = useState<string | null>(null);
  const [disableFuturePayDates, setDisableFuturePayDates] = useState(false);
  const [urlState, setUrlState] = useUrlState<MarketHistoryUrlState>(defaultState);
  const params = useMemo(() => {
    const startDate = urlState.startDate ?? defaultRange.startDate;
    const endDate = urlState.endDate ?? defaultRange.endDate;

    // validate range parameter matches dates, derive if invalid
    const isValidRange =
      urlState.range === 'custom' ||
      (urlState.range &&
        urlState.range in dateRanges &&
        dateRanges[urlState.range as keyof typeof dateRanges]?.startDate === startDate &&
        dateRanges[urlState.range as keyof typeof dateRanges]?.endDate === endDate);

    const range = isValidRange && urlState.range ? urlState.range : getRangeKey(startDate, endDate);

    return {
      endDate,
      includeFuturePayDates: urlState.includeFuturePayDates ?? defaultState.includeFuturePayDates,
      limit: urlState.limit ?? defaultState.limit,
      page: urlState.page ?? defaultState.page,
      range,
      sort: urlState.sort ?? defaultState.sort,
      sortDirection: urlState.sortDirection ?? defaultState.sortDirection,
      startDate,
    };
  }, [urlState]);
  const { endDate, includeFuturePayDates, limit, page, sort, sortDirection, startDate } = params;
  const creditNoteId = searchParams.get('creditNoteId');

  useLayoutEffect(() => {
    if (creditNoteId) {
      setCreditNoteDownloadUrl(
        `${prefixUrl}/api/c2fo/taker/${takerId}/market/${marketId}/retrieve-award-file/${creditNoteId}?token=${token}`
      );
    }
  }, [marketId, takerId, token, creditNoteId]);

  // Unsure how this is triggered, tracking to determine usage or just ported functionality from Legacy
  useEffectOnce(() => {
    // Using searchParams since unsure what types of query params are being used
    const download = searchParams.get('download');
    const payDate = searchParams.get('payDate');

    if (download) {
      datadogRum.addAction('market-history::paydate::direct-download', { download, payDate });
    }

    if (download && payDate) {
      // immediately initiate a download
      window.location.href = `${prefixUrl}/api/c2fo/taker/${takerId}/market/${marketId}/archive/${payDate}/csv?token=${token}`;
    }
  });

  const {
    data: {
      chartData,
      hasClearHistory,
      hasHistoryOutsideRange,
      minimumPayDate,
      latestPayDate,
      latestDepositDate,
      stats,
      tableData,
    },
    isFetchingChartData,
    isFetchingLatestPayDate,
    isFetchingStats,
    isFetchingTableData,
  } = useMarketHistoryData({
    params,
    takerUuid: takerMarket.takerDivisionUuid,
    marketUuid: takerMarket.marketUuid,
    makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    currency: takerMarket.currency,
  });

  // The start and end dates in URL state represent deposit dates.
  // The award file API only supports querying by pay dates, so we use the minimum and maximum pay dates
  // returned from the stats endpoint to avoid mixing deposit and pay dates when requesting the award file.
  // This ensures the invoices in the award file match those displayed in the Market History page.
  //
  // Example: User selects a deposit date range of September 20 - October 28, 2025
  // The SEA clear stats api queries the clear data with this deposit date range and returns this total clear data:
  //
  // taker_market_clear_id | cleared_invoice_amount | pay_date   | deposit_date
  // ----------------------|------------------------|------------|-------------
  // 3583218               | 3700                   | 2025-10-24 | 2025-10-28
  // 3583039               | 3700                   | 2025-09-26 | 2025-09-30
  //
  // In this case the start date should be 2025-09-26 and the end date should be 2025-10-24
  const downloadStartDate = minimumPayDate;
  const downloadEndDate = latestPayDate;

  // Disable download if: no history, still loading, or minimumPayDate is missing
  // This prevents the download button from being clicked if the minimum pay date is not available
  const isDownloadDisabled = !hasClearHistory || isFetchingLatestPayDate || isFetchingStats || !minimumPayDate;

  const awardFileDownloadLink = useMemo(
    () =>
      `${prefixUrl}/api/c2fo/taker/${takerId}/market/${marketId}/archive/csv?startDate=${downloadStartDate}&endDate=${downloadEndDate}&token=${token}`,
    [token, takerId, marketId, downloadStartDate, downloadEndDate]
  );

  const closeCreditNoteModal = () => {
    setCreditNoteDownloadUrl(null);
    searchParams.delete('creditNoteId');
    navigate({
      pathname,
      search: searchParams.toString(),
    });
  };

  const handleCreditNoteDownload = () => {
    window.location.href = creditNoteDownloadUrl!;
    trackEnterpriseEvent('history-credit-note-request::submitted');
    closeCreditNoteModal();
  };

  const clearFilters = () => {
    setUrlState((f) => ({
      ...defaultState,
      includeFuturePayDates: f.includeFuturePayDates,
    }));
  };

  const includeFuturePayDatesText = t('taker.history.includeFutureDepositDates');

  return (
    <>
      <CreditNoteModal
        handleDownload={handleCreditNoteDownload}
        onClose={closeCreditNoteModal}
        open={!!creditNoteDownloadUrl}
      />
      <HistoryFilter
        filterOpen={filterOpen}
        onDateRangeChange={({ range: { startDate: newStartDate, endDate: newEndDate }, rangeKey }) => {
          const today = dateAsIsoString();
          const includeFuturePayDates = newEndDate >= today;

          setDisableFuturePayDates(newEndDate < today);
          setUrlState({
            ...urlState,
            startDate: newStartDate,
            endDate: newEndDate,
            range: rangeKey,
            includeFuturePayDates,
            page: 1,
          });
          trackEnterpriseEvent('history-filter::clicked', { startDate: newStartDate, endDate: newEndDate });
        }}
        setFilterOpen={setFilterOpen}
        startDate={startDate}
        endDate={endDate}
        rangeKey={params.range}
      />
      <div>
        <div className="flex flex-col gap-4 bg-gray-100 px-4 py-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button onClick={() => setFilterOpen(true)}>{t('core.filters')}</Button>
              {/* always 1 filter applied */}
              <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white">
                1
              </div>
            </div>
            <Button onClick={clearFilters} variant="tertiary">
              {t('core.clearFilters')}
            </Button>
          </div>
          <div>
            <Checkbox
              id={'includeFuturePayDates'}
              color="secondary"
              label={
                <span className="flex items-center gap-2 text-sm">
                  <span className={cn({ 'text-text-disabled': disableFuturePayDates })}>
                    {includeFuturePayDatesText}
                  </span>
                  {disableFuturePayDates && (
                    <Popover>
                      <Popover.Trigger>
                        <InfoCircleIcon className="fill-text-secondary" />
                      </Popover.Trigger>
                      <Popover.Content arrow side="bottom">
                        <div className="text-sm">{t('taker.history.includeFuturePayDatesDisabledLabel')}</div>
                      </Popover.Content>
                    </Popover>
                  )}
                </span>
              }
              checked={includeFuturePayDates}
              disabled={disableFuturePayDates}
              onChange={(e) => {
                setUrlState({ ...urlState, includeFuturePayDates: e.target.checked });
                trackEnterpriseEvent('history-include-future-pay-dates::clicked', { value: e.target.checked });
              }}
            />
          </div>
        </div>
        <div className="px-4">
          <RangeHeader
            awardFileDownloadLink={awardFileDownloadLink}
            endDate={endDate}
            includeFuturePayDates={includeFuturePayDates}
            isDownloadDisabled={isDownloadDisabled}
            latestPayDate={latestPayDate}
            latestDepositDate={latestDepositDate}
            startDate={startDate}
            rangeKey={params.range}
          />
          <HistoryStatsBar
            clearedDiscountWeightedAvg={stats?.clearedDiscountWeightedAvg}
            clearedEarn={stats?.clearedEarn}
            clearedInvoiceAmount={stats?.clearedInvoiceAmount}
            currency={takerMarket.currency}
            loading={isFetchingStats}
          />
          <div className="hidden py-4 pt-8 lg:flex">
            <HistoryChart
              currency={takerMarket.currency}
              data={chartData ?? []}
              endDate={endDate}
              loading={isFetchingChartData}
              startDate={startDate}
            />
          </div>
        </div>
        <div className="flex w-full flex-col overflow-hidden">
          <HistoryTable
            data={tableData ?? []}
            sort={sortDirection === 'desc' ? `-${sort}` : sort}
            setSort={(sortKey) => {
              const [, desc, sort] = sortKey.match(/^(-)?(.*)$/) ?? [];
              setUrlState({ ...urlState, sort, sortDirection: desc ? 'desc' : 'asc', page: 1 });
              trackEnterpriseEvent('history-sort::clicked', { key: sortKey });
            }}
            pageIndex={page}
            pageSize={limit}
            onPageChange={(page) => {
              setUrlState({ ...urlState, page });
              trackEnterpriseEvent('history-page::clicked');
            }}
            onPageSizeChange={(limit) => {
              setUrlState({ ...urlState, limit });
              trackEnterpriseEvent('history-page-size::clicked', { size: limit });
            }}
            historyType="market"
            loading={isFetchingTableData}
            hasHistoryOutsideRange={hasHistoryOutsideRange}
            onOpenFilters={() => setFilterOpen(true)}
          />
        </div>
      </div>
    </>
  );
};

const MarketHistory = () => {
  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <div className="space-y-6 px-4 py-8">
          <Skeleton className="mb-4 h-40 w-full" />
          <Skeleton className="h-[352px] w-full" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="mb-2 h-16 w-full" />
          ))}
        </div>
      )}
    >
      <MarketHistoryComponent />
    </QueryBoundaries>
  );
};

export default MarketHistory;
